import { useState, useCallback, useEffect, useRef } from "react";

interface MousePosition {
  x: number;
  y: number;
}

// gets mouse position relative to the given element
export function useMouse<T extends HTMLElement = HTMLElement>() {
  const [position, setPosition] = useState<MousePosition>({ x: 0, y: 0 });
  const [element, setElement] = useState<T | null>(null);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) setElement(ref.current);
  }, []);

  const handleMouseMove = useCallback(
    (event: Event) => {
      if (element) {
        const relativePos = getRelativeMousePos(event, element);
        setPosition(relativePos);
      }
    },
    [element],
  );

  useEffect(() => {
    if (element) {
      element.addEventListener("mousemove", handleMouseMove);
      return () => {
        element.removeEventListener("mousemove", handleMouseMove);
      };
    }
  }, [element, handleMouseMove]);

  return { ref, ...position };
}

const getRelativeMousePos = (
  event: Event,
  element: HTMLElement,
): MousePosition => {
  const mouseEvent = event as MouseEvent;
  const rect = element.getBoundingClientRect();
  // Calculate coordinates relative to the element
  return {
    x: Math.max(0, Math.round(mouseEvent.clientX - rect.left)),
    y: Math.max(0, Math.round(mouseEvent.clientY - rect.top)),
  };
};
